import { GetAllInducteesQuery } from "@/__generated__/graphql";
import { debounce } from "lodash";
import CaretIcon from "@/assets/svg/arrow-right.svg";
import { groupInducteesByYear } from "@/core/utils";
import { useInducteeLanding } from "@/providers/InducteeLandingContext";
import { Container, IconButton } from "@components";
import className from "classnames/bind";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollToDiscoverIcon from "@/assets/svg/scroll-to-discover.svg";
import SwipeToDiscoverIcon from "@/assets/svg/swipe-to-discover.svg";
import ArrowRightSwipe from "@/assets/svg/arrow-right-swipe.svg";
import { AnimatePresence } from "motion/react";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import InducteesByClassList from "./InducteeByClassTimelineList";

import { useHeader } from "@/providers/HeaderContext";
import { useViewSize } from "@/providers/ViewSizeContext";
import { useGSAP } from "@gsap/react";
import { useLenis } from "lenis/react";
import styles from "../InducteesByClass.module.scss";
import InducteesByClassGrid from "./InducteeByClassGrid";
import Timeline from "./Timeline";
import Lenis from "lenis";
import { useClientMount } from "@/providers/ClientMountProvider";

const cx = className.bind(styles);

interface InducteesByClassProps {
  inductees: GetAllInducteesQuery["allInductees"];
  primaryCardCta: CTAData;
  onSelectYear: (year?: string) => void;
}

export default function InducteesByClassTimeline({
  inductees,
  primaryCardCta,
  onSelectYear,
}: InducteesByClassProps) {
  const [activeYear, setActiveYear] = useState<string | null>(null);
  const listRowEl = useRef<HTMLDivElement>(null);
  const carouselEl = useRef<HTMLDivElement>(null);
  const spacerEl = useRef<HTMLDivElement>(null);
  const gridEl = useRef<HTMLDivElement>(null);
  const scrollEl = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSwiped, setIsSwiped] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLButtonElement>(null);
  const lenisRef = useRef<Lenis>(null);
  const carouselControls = useRef<HTMLDivElement>(null);
  const snapRefs = useRef<number[]>([]);
  const spacerWidth = useRef(0);
  const [snapIndex, setSnapIndex] = useState(0);
  const [carouselClass, setCarouselClass] = useState('');
  const [dragProgress, setDragProgress] = useState(0);
  const lastScrollCoef = useRef(0);
  const lastYear = useRef<string | null>(null);
  const cardScrollBehavior = useRef<string>("instant");
  const { viewWidth, viewHeight, isMobile, isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();
  const listWidth = useRef(0);
  const isDragging = useRef(false);
  const [cardWidth, setCardWidth] = useState(0);
  const [isRetracted, setIsRetracted] = useState(false);
  const containerEl = useRef<HTMLDivElement>(null);
  let animated = useRef(false);
  const { setIsHeaderFixed } = useHeader();

  const { scrollCoef, setScrollCoef, tabAffix, setExtraFixedTab } =
    useInducteeLanding();
  const groupedInductees = useMemo(() => {
    return groupInducteesByYear(inductees);
  }, [inductees]);
  const years = useMemo(() => {
    return Object.keys(groupedInductees).sort((a, b) => {
      const yearA = parseInt(a, 10);
      const yearB = parseInt(b, 10);
      return yearB - yearA;
    });
  }, [groupedInductees]);

  const onYearClick = useCallback(
    (year: string) => {
      if (isDragging.current) {
        isDragging.current = false;
        return;
      }

      cardScrollBehavior.current = activeYear ? "smooth" : "instant";

      let retract = activeYear !== year;
      lastYear.current = year;
      if (activeYear === year) {
        setActiveYear(null);

        if (isDesktop) {
          containerEl.current.style.height = "1000vh";
          gsap.to(carouselEl.current, { opacity: 0, duration: 0 });
        }

        setExtraFixedTab(false);

        onSelectYear(null);
        lenisRef.current?.stop();
        gsap.delayedCall(0.5, () => {
          lenisRef.current?.start();
          setIsRetracted(retract);
        });
      } else {
        setExtraFixedTab(true);
        lenisRef.current?.scrollTo(0, { immediate: true });
        lenisRef.current?.stop();

        if (isDesktop) {
          containerEl.current.style.height = "auto";
        }

        setActiveYear(year);
        onSelectYear(year);
        if (!isRetracted) {
          gsap.to(carouselEl.current, { opacity: 0, duration: 0 });
        }
        gsap.delayedCall(!isRetracted ? 0.3 : 1, () => {
          lenisRef.current?.start();
          setIsRetracted(retract);
        });
      }

      //toggle timeline
      gsap.to(timelineRef.current, {
        autoAlpha: retract ? 0 : 1,
        duration: 0.3,
        ease: "power2.out",
      });

      if (retract) {
        gsap.set(dragRef.current, {
          opacity: 0,
        });
      }

      gsap.to(dragRef.current, {
        duration: retract ? 0 : 1,
        autoAlpha: retract ? 0 : 1,
        delay: retract ? 0 : 1,
        ease: "power2.out",
      });

      setIsScrolled(true);

      const index = snapRefs.current.indexOf(parseFloat(year));
      setSnapIndex(index);
    },
    [mounted, isReady, activeYear, isDesktop, isRetracted]
  );

  useEffect(() => {
    const focusForm = () => {
      const footer = document.querySelector("footer");
      const nextFocusable = footer?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) as HTMLElement;

      if (nextFocusable) {
        nextFocusable?.focus();
        footer.scrollIntoView();
      }
    };

    const handleCarouselKeydown = (event: KeyboardEvent) => {
      const scrollToYear = (year: string) => {
        const index = [...years].reverse().indexOf(year);
        const coef = (years.length - index - 1) * (1 / (years.length - 1));

        lenisRef.current?.scrollTo(viewHeight * 9 * coef);
      };

      const setGridFocus = () => {
        (document.querySelector(".grid-el a") as HTMLAnchorElement)?.focus();
      };

      if (event.key === "Tab") {
        event.preventDefault();

        if (event.shiftKey) {
          const tabs = document.querySelectorAll("#inductees-tab");
          tabs.forEach((t: HTMLDivElement) => {
            t.focus();
          });

          return;
        }

        if (activeYear) {
          setGridFocus();
          return;
        }

        focusForm();
      } else if (event.key === "ArrowRight") {
        const next = document.activeElement?.parentElement
          ?.nextSibling as HTMLElement;

        if (next) {
          const elm = next.children[0] as HTMLElement;
          if (elm) {
            elm.focus();
            if (!activeYear) {
              scrollToYear(next.dataset.year);
            }
          }
        }
      } else if (event.key === "ArrowLeft") {
        const previous = document.activeElement?.parentElement
          ?.previousSibling as HTMLElement;

        if (previous) {
          const elm = previous.children[0] as HTMLElement;
          if (elm) {
            elm.focus();
            if (!activeYear) {
              scrollToYear(previous.dataset.year);
            }
          }
        }
      } else if (event.key === "Enter") {
        setTimeout(() => {
          setGridFocus();
        }, 300);
      }
    };

    carouselEl.current?.addEventListener("keydown", handleCarouselKeydown);

    return () => {
      carouselEl.current?.removeEventListener("keydown", handleCarouselKeydown);
    };
  }, [viewHeight, activeYear]);

  const alignToYear = (year: string) => {
    const el = carouselEl.current.querySelector(`[data-year="${year}"]`);

    setTimeout(() => {
      el?.scrollIntoView({
        behavior: cardScrollBehavior.current as ScrollBehavior,
        block: "center",
        inline: "center",
      });
    }, 150);
  };

  useEffect(() => {
    setIsHeaderFixed(true);
    return () => {
      setIsHeaderFixed(false);
    };
  }, []);

  useEffect(() => {
    const animateControls = (isActive: boolean) => {
      if (!carouselControls.current) return;
      gsap.to(carouselControls.current, {
        autoAlpha: isActive ? 1 : 0,
        duration: 0.3,
        delay: !isActive ? 0.5 : 0.3,
        ease: "power2.out",
      });
    };

    animateControls(tabAffix || !!activeYear);
  }, [tabAffix, activeYear]);

  useEffect(() => {
    if (isRetracted && activeYear) {
      alignToYear(activeYear);
    } else if (!isRetracted && lastYear.current && activeYear === null) {
      onTimelineMarkClick(
        years.length - years.indexOf(lastYear.current) - (isDesktop ? 0 : 1),
        true
      );
    }
  }, [activeYear, isRetracted, isDesktop]);

  useEffect(() => {
    gsap.to(carouselEl.current, { opacity: 1, delay: isRetracted ? 0.3 : 0.4 });
  }, [isRetracted]);

  const onTimelineDragStart = () => {};

  const onTimelineDrag = useCallback(
    (drag: Draggable) => {
      const { x, maxX, minX } = drag;
      const percentage = (x - minX) / (maxX - minX);

      if (isDesktop) {
        let maxScroll = viewHeight * 9;
        lenisRef.current?.scrollTo(maxScroll * percentage, { immediate: true });
      } else {
        const gap = 25;
        const maxScroll =
          (carouselEl.current.children[1].getBoundingClientRect().width + gap) *
          (years.length - 1);
        carouselEl.current.scrollLeft = maxScroll * percentage;
      }
    },
    [cardWidth, listWidth, isDesktop, viewHeight]
  );
  const onTimelineMarkClick = (index: number, immediate = false) => {
    if (isDesktop) {
      const coef = (years.length - index - 1) * (1 / (years.length - 1));
      lenisRef.current?.scrollTo(viewHeight * 9 * coef, { immediate });
    } else {
      carouselEl.current.children[years.length - index].scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  const lenis = useLenis(
    ({ scroll }) => {
      const scrollBound = 10;
      if (!activeYear) {
        if (scroll > scrollBound) {
          setIsScrolled(true);
        }
        if (isDesktop) {
          setScrollCoef(Math.max(0, Math.min(1, scroll / viewHeight / 9)));
        }
      }
    },
    [viewHeight, activeYear, isDesktop]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    lenis?.scrollTo(0, { immediate: true });
  }, [lenis]);

  const handleClickArrow = useCallback(
    (direction: "prev" | "next") => {
      if (activeYear) {
        const newSnapIndex =
          direction === "next" ? snapIndex + 1 : snapIndex - 1;
        if (newSnapIndex < 0 || newSnapIndex >= snapRefs.current.length) return;
        const newSnap = snapRefs.current[newSnapIndex];

        cardScrollBehavior.current = "smooth";

        setActiveYear(newSnap.toString());
        setSnapIndex(newSnapIndex);
      } else {
        if (lenis) {
          lenis.scrollTo(
            lenis.actualScroll + (direction === "next" ? 150 : -150)
          );
        }
      }
    },
    [snapIndex, snapRefs, activeYear, lenis]
  );

  useEffect(() => {
    if (!isDesktop) return;

    const clampedScrollCoef = Math.min(1, scrollCoef);
    let target = (listWidth.current - viewWidth + 60) * clampedScrollCoef;
    gsap.set(carouselEl.current, {
      x: -target,
    });

    if (years.length > 0) {
      snapRefs.current = years.map((el) => parseFloat(el));
    }

    // animates the 'separation'
    if (!animated.current) {
      return;
    }
    // const movingToRight = clampedScrollCoef > lastScrollCoef.current;
    // let { velocity } = lenis;
    // const diffCoef = 1 / years.length;
    // let closestIndex = Math.max(0, Math.round(clampedScrollCoef / diffCoef));

    // if (!movingToRight) {
    //   closestIndex += 2;
    // }

    // if (lenisRef.current?.isScrolling !== "smooth") {
    //   velocity = 0;
    // }

    // let factor = 5;
    // if (viewWidth > 1440) {
    //   factor = 10;
    // }

    // if (!activeYear) {
    //   if (scrollCoef < 0.9) {
    //     Array.from(carouselEl.current.children).forEach((el, index) => {
    //       if (el.children.length == 0) return;

    //       gsap.killTweensOf(el.children[0]);

    //       if (index < closestIndex && !movingToRight) {
    //         gsap.to(el.children[0], {
    //           x: Math.abs(index - closestIndex) * factor * velocity,
    //           duration: 0.5,
    //           opacity: 1,
    //           height: "auto",
    //           ease: "power4.out",
    //         });
    //       } else if (index > closestIndex && movingToRight) {
    //         gsap.to(el.children[0], {
    //           x: Math.abs(index - closestIndex) * factor * velocity,
    //           duration: 0.5,
    //           opacity: 1,
    //           height: "auto",
    //           ease: "power4.out",
    //         });
    //         gsap.to(el, { opacity: 1 });
    //       } else {
    //         gsap.to(el.children[0], {
    //           x: 0,
    //           duration: 0.5,
    //           opacity: 1,
    //           height: "auto",
    //           ease: "power4.out",
    //         });
    //       }
    //     });
    //   } else {
    //     Array.from(carouselEl.current.children).forEach((el, index) => {
    //       if (el.children.length == 0) return;
    //       gsap.killTweensOf(el.children[0]);
    //       gsap.to(el.children[0], {
    //         x: 0,
    //         duration: 0.5,
    //         opacity: 1,
    //         ease: "power4.out",
    //       });
    //     });
    //   }
    // }

    lastScrollCoef.current = clampedScrollCoef;
  }, [scrollCoef, viewWidth, listWidth, isDesktop]);

  useEffect(() => {
    const spacerWidthEl =
      listRowEl.current.children[0].children[0].getBoundingClientRect().width;
    const cardWidth =
      listRowEl.current.children[0].children[1].getBoundingClientRect().width;
    setCardWidth(cardWidth);
    listWidth.current =
      spacerWidthEl +
      (cardWidth + 30) * (carouselEl.current.children.length - 1) +
      containerEl.current.getBoundingClientRect().left * 2;

    spacerWidth.current = spacerWidthEl;
  }, [viewWidth, carouselEl.current]);

  useEffect(() => {
    if (!mounted || !isReady) return;
    if (isDesktop) {
      containerEl.current.style.height = "1000vh";
    } else {
      containerEl.current.style.height = "auto";
    }

    if (isDesktop) {
      gsap.fromTo(
        carouselEl.current,
        { opacity: 0, x: "20vw" },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: "power3.out",
          onComplete: () => {
            animated.current = true;
            lenisRef.current?.start();
          },
        }
      );
    } else {
      gsap.fromTo(
        carouselEl.current,
        {
          opacity: 0,
          x: -30,
        },
        {
          x: 0,
          opacity: 1,
          stagger: 0.1,
          ease: "power4.out",
          duration: 1,
          onComplete: () => {
            lenisRef.current?.start();
          },
        }
      );
    }
  }, [mounted, isReady, carouselEl, isDesktop]);

  useGSAP(
    () => {
      if (!mounted || !isReady) return;
      gsap.fromTo(
        scrollEl.current.children[0],
        {
          opacity: 0,
          scaleX: 0.3,
          scaleY: 0.3,
        },
        {
          scaleX: 1,
          scaleY: 1,
          opacity: 1,
          duration: 0.4,
          ease: "power3.out",
          delay: 0.3,
        }
      );
    },
    { dependencies: [mounted, isReady, scrollEl] }
  );

  useGSAP(
    () => {
      if (!mounted || !isReady) return;
      gsap.fromTo(
        timelineRef.current,
        {
          y: 50,
          opacity: 0,
        },
        { y: 0, opacity: 1 }
      );
    },
    { dependencies: [mounted, isReady, timelineRef] }
  );

  const handleMouseDown = useCallback(
    (event: SyntheticEvent) => {
      if (activeYear) {
        return;
      }

      isDragging.current = false;

      const startScroll = isDesktop
        ? lenisRef.current?.scroll
        : carouselEl.current.scrollLeft;
      const startX = (event.nativeEvent as MouseEvent).clientX;

      const handleMouseMove = (event: MouseEvent) => {
        // remove scroll snap when dragging
        if (Math.abs(event.clientX - startX) > 1) {
          isDragging.current = true;
        }

        carouselEl.current.style.scrollSnapType = "unset";

        const finalScroll = startScroll - (event.clientX - startX);
        document.body.style.cursor = "-webkit-grabbing";

        if (isDesktop) {
          lenisRef.current?.scrollTo(finalScroll, {});
        } else {
          // carouselEl.current.scrollLeft = finalScroll;
          gsap.to(carouselEl.current, { scrollLeft: finalScroll });
        }
      };

      const handleMouseUp = () => {
        clearListeners();
      };

      const clearListeners = () => {
        document.body.style.cursor = "auto";
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("mouseleave", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("mouseleave", handleMouseUp);

      return clearListeners;
    },
    [activeYear, isDesktop]
  );

  useEffect(() => {
    if (!mounted || !isReady || !isDesktop) return;

    let trigger: ScrollTrigger = null;
    if (containerEl.current) {
      trigger = ScrollTrigger.create({
        trigger: containerEl.current,
        start: "bottom bottom",
        end: "bottom top",
        onUpdate: (self) => {
          const elements = [timelineRef.current, carouselEl.current];

          if (!activeYear) {
            gsap.set(elements, {
              y: -self.progress * viewHeight,
            });
          }
        },
      });
    }

    return () => {
      trigger?.kill();
    };
  }, [mounted, isReady, containerEl.current, viewHeight, activeYear, isDesktop]);

  const carouselScroll = debounce(() => {
    if (isDesktop) return;
    const gap = 25;
    const maxScroll =
      (carouselEl.current.children[1].getBoundingClientRect().width + gap) *
      (years.length - 1);
    const scrollCoef = Math.max(
      0,
      Math.min(1, carouselEl.current.scrollLeft / maxScroll)
    );
    setScrollCoef(scrollCoef);
    if (scrollCoef > 0.02) {
      setIsSwiped(true);
    }
  }, 5);
  useEffect(() => {
    lenisRef.current = lenis;
  }, [lenis]);

  useEffect(() => {
    setCarouselClass(isDesktop ? "is-scrolled" : "");
  }, [isDesktop])

  return (
    <Container className={cx("inductees-by-class-timeline")} ref={containerEl}>
      <div
        className={cx("list-row", { "year-opened": activeYear })}
        ref={listRowEl}
        tabIndex={-1}
        onMouseDown={handleMouseDown}
      >
        <div
          className={cx("carousel", carouselClass)}
          ref={carouselEl}
          style={{ opacity: 0 }}
          onScroll={carouselScroll}
        >
          <div
            className={cx("carousel-spacer")}
            ref={spacerEl}
            key="spacer-1"
            id="carousel-spacer"
          />
          {years.map((year, index) => (
            <div
              className={cx("inductees-by-class-list-item")}
              data-year={year}
              key={`${year}-${index}`}
            >
              <InducteesByClassList
                key={`${year}-${index}`}
                year={year}
                activeYear={activeYear}
                primaryCardCta={!index ? primaryCardCta : null}
                onYearClick={onYearClick}
                inductees={groupedInductees[year]}
              />
            </div>
          ))}
        </div>
      </div>
      <div
        ref={scrollEl}
        className={cx("scroll-to-discover", {
          "scroll-to-discover--hidden": isScrolled,
        })}
      >
        <ScrollToDiscoverIcon className={cx("icon-desktop")} />
      </div>
      {activeYear === null && (
        <div
          ref={scrollEl}
          className={cx("scroll-to-discover", {
            "scroll-to-discover--hidden": isSwiped || activeYear,
            swipe: true,
          })}
        >
          <SwipeToDiscoverIcon className={cx("icon-mobile")} />
          <ArrowRightSwipe className={cx("icon-mobile-arrow")} />
        </div>
      )}

      <AnimatePresence mode="wait">
        {activeYear && (
          <InducteesByClassGrid
            exit={{ opacity: 0 }}
            listEl={listRowEl.current}
            transition={{
              duration: 0.2,
              ease: "linear",
            }}
            onAnimationComplete={() => {}}
            inductees={groupedInductees[activeYear]}
            ref={gridEl}
            key={activeYear}
            year={activeYear}
          />
        )}
      </AnimatePresence>
      {years.length && (
        <Timeline
          startYear={parseFloat(years[0])}
          onDragStart={onTimelineDragStart}
          onTimelineDrag={onTimelineDrag}
          dragProgress={dragProgress}
          ref={timelineRef}
          dragRef={dragRef}
          onTimelineMarkClick={onTimelineMarkClick}
          endYear={parseFloat(years[years.length - 1])}
        />
      )}
      <div
        className={cx("carousel-controls", {
          "is-scrolled": true,
          "active-year": !!activeYear,
        })}
        ref={carouselControls}
      >
        <IconButton
          size="small"
          icon={CaretIcon}
          disabled={snapIndex <= 0 && !!activeYear}
          className={cx("previous")}
          onClick={() => handleClickArrow("prev")}
          tabindex={-1}
          ariaLabel="“Move timeline backward"
        />
        <IconButton
          size="small"
          icon={CaretIcon}
          className={cx("next")}
          onClick={() => handleClickArrow("next")}
          disabled={snapIndex >= snapRefs.current.length - 1 && !!activeYear}
          tabindex={-1}
          ariaLabel="Move timeline forward"
        />
      </div>
    </Container>
  );
}
